import axios from "axios";
import { useEffect, useState, useCallback, useRef } from "react";
import { API } from "../../lib/api";

// Command Center reuses the SAME admin identity/token — no second auth system.
export const ccToken = () => localStorage.getItem("taven_admin_token");

const CC_BASE = `${API}/v1/command-center`;

export const ccClient = () =>
  axios.create({ baseURL: CC_BASE, headers: { Authorization: `Bearer ${ccToken()}` } });

// Extract a truthful error code/message from the ApiResponse error envelope.
export const ccErrorInfo = (error) => {
  if (axios.isCancel(error)) return null;
  const status = error?.response?.status;
  const env = error?.response?.data;
  const code = env?.error?.code || (status === 401 ? "UNAUTHENTICATED" : status === 403 ? "FORBIDDEN" : "ERROR");
  const message = env?.error?.message || error?.message || "Request failed";
  return { status, code, message };
};

// Serialize the scope + date range into query params expected by the backend.
export const scopeParams = (scope, range) => {
  const p = { scope: scope.scope };
  if (scope.marketplace_id) p.marketplace_id = scope.marketplace_id;
  if (scope.seller_org_id) p.seller_org_id = scope.seller_org_id;
  if (scope.store_id) p.store_id = scope.store_id;
  if (range?.date_from) p.date_from = range.date_from;
  if (range?.date_to) p.date_to = range.date_to;
  return p;
};

// Data hook: fetches a CC endpoint, returns truthful loading/error/data + meta.
export function useCC(path, params, deps = []) {
  const [state, setState] = useState({ loading: true, data: null, meta: null, error: null });
  const controllerRef = useRef(null);

  const load = useCallback(() => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setState((s) => ({ ...s, loading: true, error: null }));
    ccClient()
      .get(path, { params, signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        setState({ loading: false, data: data?.data ?? null, meta: data?.meta ?? null, error: null });
      })
      .catch((error) => {
        const info = ccErrorInfo(error);
        if (info === null) return; // canceled
        setState({ loading: false, data: null, meta: null, error: info });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, JSON.stringify(params)]);

  useEffect(() => {
    const cancel = load();
    return cancel;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, ...deps]);

  return { ...state, refetch: load };
}
